import { useApolloClient } from '@apollo/client';
import { useI18n } from '@i18n';
import { useSelector } from '@xstate/store-react';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import { sessionStore } from '@/components/session/session.store';
import { GET_BULK_ACTION_PARAMETERS } from '@/graphql/ActionPlanning.queries';

// RED-2822: provisional — replace with server-push notifications once implemented,
// then remove this poller, PendingBulkActionsPoller, and sessionStore.pendingBulkActionIds.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const usePendingBulkActionsPoller = () => {
  const pendingBulkActionIds = useSelector(sessionStore, (s) => s.context.pendingBulkActionIds);
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();

  const checkAll = useCallback(async () => {
    for (const id of pendingBulkActionIds) {
      try {
        const { data } = await client.query({
          query: GET_BULK_ACTION_PARAMETERS,
          variables: { id },
          fetchPolicy: 'network-only',
        });

        const name = data?.bulk_actions_overview?.[0]?.bulk_action?.name;
        const total = data?.total_count?.aggregate?.count ?? 0;
        const planned = data?.planned_count?.aggregate?.count ?? 0;
        const notPlanned = data?.not_planned_count?.aggregate?.count ?? 0;
        const deletionFailed = data?.deletion_failled_count?.aggregate?.count ?? 0;

        const remaining = total - planned - notPlanned - deletionFailed;

        if (total > 0 && remaining <= 0) {
          enqueueSnackbar(
            t('ActionPlanningScenario_BulkActionModal_ProcessingCompletedNotification', {
              name: name ?? '',
            }),
            { variant: 'success', autoHideDuration: SnackbarTimeouts.Info },
          );
          sessionStore.send({ type: 'dismissPendingBulkAction', id });
        }
      } catch {
        // silently ignore transient poll failures
      }
    }
  }, [pendingBulkActionIds, client, enqueueSnackbar, t]);

  useEffect(() => {
    if (pendingBulkActionIds.length === 0) return;
    checkAll();
    const interval = setInterval(checkAll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pendingBulkActionIds, checkAll]);
};
